import { AudioFilled, PauseOutlined } from "@ant-design/icons";
import { Avatar, Button, Card, Col, Flex, Row, Select } from "antd";
import React from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { setRecordingDevice } from "../../slices/recordingsSlice";
import { EventType, trackEvent } from "../../utils/analytics";
import { isTooShort } from "../../utils/recording.utils";
import { getRandomAvatarColor } from "../../utils/ui.utils";
import CustomAudioVisualizer from "./CustomAudioVisuazlier";
import styles from "./activerecordingview.module.scss";

type ActiveRecordingViewProps = {
  isPaused: boolean;
  recordingTime: number;
  handlePauseOrEndRecording: () => void;
  handleResumeRecording: () => void;
  handleSubmitAudio: () => void;
  cancelRecording: () => void;
};

const ActiveRecordingView = ({
  isPaused,
  recordingTime,
  handlePauseOrEndRecording,
  handleResumeRecording,
  handleSubmitAudio,
  cancelRecording,
}: ActiveRecordingViewProps) => {
  const componentName = "ActiveRecordingView";

  const { isSubmissionInProgress, audioDevices } = useAppSelector((state) => state.appState);
  const { recordingDetail, mediaRecorder, recordingDevice, withHeadphones } = useAppSelector(
    (state) => state.recordings,
  );
  const { clients } = useAppSelector((state) => state.client);
  const dispatch = useAppDispatch();

  const handleDraftRecording = () => {
    trackEvent(EventType.SAVE_AS_DRAFT, {
      recordingTime: recordingTime,
    });
    // This is to ensure the event is tracked before the page is reloaded
    setTimeout(() => {
      window.location.reload();
    }, 0);
  };

  const client = React.useMemo(() => {
    return clients.find((client) => client.id === recordingDetail?.client_id);
  }, [recordingDetail, clients]);

  return (
    <Card className={styles[`${componentName}__container-right-container`]}>
      <Row>
        {recordingDetail?.client_id && client && (
          <Col className={styles[`${componentName}__container-right-user`]}>
            <span>
              <Avatar
                style={{
                  backgroundColor: getRandomAvatarColor(client.name),
                }}
                size={"small"}
              >
                {client.name[0].toUpperCase()}
              </Avatar>{" "}
              &nbsp;
              {client.name}
            </span>
          </Col>
        )}
        <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
          <Card className={`${styles[`${componentName}__container-right-recording-card`]}`}>
            {!isPaused && (
              <div className={styles[`${componentName}__modal-logo`]}>
                <AudioFilled style={{ fontSize: "20px", color: "#EF4444" }} />
              </div>
            )}
            {isPaused && (
              <Flex justify="center">
                <PauseOutlined
                  style={{
                    fontSize: "25px",
                    color: "var(--grey-text)",
                    margin: "5px 0px",
                  }}
                />
              </Flex>
            )}
            <Flex className={styles[`${componentName}__card-right-text`]} justify="center" vertical gap={5}>
              <Flex justify="center" align="center" gap={10} className={styles[`${componentName}__container-text`]}>
                {isPaused ? "Captured" : "Capturing"} session
              </Flex>

              <span className={styles[`${componentName}__modal-sub-text`]}>
                {Math.floor(recordingTime / 60)}:{String(recordingTime % 60).padStart(2, "0")} (max: 120 mins)
              </span>

              {mediaRecorder && !isPaused ? (
                <div>
                  <CustomAudioVisualizer
                    mediaRecorder={mediaRecorder}
                    width={200}
                    height={25}
                    barColor="rgb(17, 185, 129)"
                  />
                </div>
              ) : null}
              {audioDevices && audioDevices.length > 0 && (
                <>
                  <Flex justify="center">Audio Input: </Flex>
                  <Select
                    className="w-full"
                    options={audioDevices.map((device) => ({
                      label: device.label || device.deviceId,
                      value: device.deviceId,
                    }))}
                    value={recordingDevice?.deviceId}
                    onChange={(value) => {
                      const selectedDevice = audioDevices.find((device) => device.deviceId === value);
                      if (selectedDevice) {
                        dispatch(setRecordingDevice(selectedDevice));
                      }
                    }}
                    disabled={withHeadphones}
                  />
                </>
              )}
            </Flex>
          </Card>
        </Col>
        <Col span={24} className={`${styles[`${componentName}__modal-row`]}`}>
          <Row gutter={[10, 10]}>
            <Col span={12}>
              <Button
                disabled={isPaused && isSubmissionInProgress}
                className={`${styles[`${componentName}__modal-row-button`]} ${
                  styles[`${componentName}__modal-row-button-big`]
                }`}
                onClick={isPaused ? handleResumeRecording : handlePauseOrEndRecording}
              >
                {isPaused ? "Resume" : "Pause"}
              </Button>
            </Col>
            <Col span={12}>
              <Button
                disabled={(isPaused && isSubmissionInProgress) || isTooShort(recordingTime)}
                className={`${styles[`${componentName}__modal-row-button`]} ${
                  styles[`${componentName}__modal-row-button-big`]
                }`}
                onClick={handleDraftRecording}
              >
                Save as Draft
              </Button>
            </Col>

            <Col span={24}>
              {isPaused ? (
                <Button
                  loading={isSubmissionInProgress}
                  disabled={isSubmissionInProgress}
                  type="primary"
                  className={`${styles[`${componentName}__modal-row-button`]} ${
                    styles[`${componentName}__modal-row-button-big`]
                  }`}
                  onClick={handleSubmitAudio}
                >
                  Submit Audio
                </Button>
              ) : (
                <Button
                  type="primary"
                  className={`${styles[`${componentName}__modal-row-button`]} ${
                    styles[`${componentName}__modal-row-button-big`]
                  }`}
                  onClick={handlePauseOrEndRecording}
                >
                  End Session
                </Button>
              )}
            </Col>
          </Row>
          <Flex gap={10} justify="center" className={`${styles[`${componentName}__modal-row-secondary`]}`}>
            <span onClick={cancelRecording}>Cancel recording</span>
          </Flex>
        </Col>
      </Row>
    </Card>
  );
};

export default ActiveRecordingView;
